import { useEffect, useState } from 'react';
import { ApiError, get, post } from '../api.js';
import { t } from '../i18n.js';
import { mmss } from '../util.js';

export type AiTask = 'proofread' | 'titles' | 'shorten' | 'summary' | 'translate';

export interface AiStatus {
  enabled: boolean;
  provider: string;
  model: string;
  tasks: AiTask[];
}

interface AiResult {
  task: AiTask;
  model: string;
  text?: string;
  changes?: Array<{ from: string; to: string; reason: string }>;
  titles?: string[];
  summary?: string;
  notes?: string;
  usage: { inputTokens: number; outputTokens: number };
}

let statusCache: Promise<AiStatus> | null = null;
/** Asked once per page load; the panel and its toolbar button share it. */
export function aiStatus(): Promise<AiStatus> {
  if (!statusCache) statusCache = get<AiStatus>('/api/ai/status').catch(() => ({ enabled: false, provider: 'unknown', model: '', tasks: [] }));
  return statusCache;
}

const WORDS_PER_SECOND = 3.3;
const countWords = (s: string) => (s.match(/\S+/g) ?? []).length;

interface Props {
  /** The script as plain text, paragraphs separated by blank lines. */
  getText: () => string;
  title: string;
  readOnly: boolean;
  /** Replace the script with a proposal (the journalist pressed Εφαρμογή). */
  onApplyText: (text: string) => void;
  onApplyTitle: (title: string) => void;
  onClose: () => void;
}

/**
 * AI help in the story editor: proofreading, titles, shortening to a reading time, a web summary,
 * translation. Every answer is a proposal shown next to the script; nothing changes until the
 * journalist applies it, and the server stores no text.
 */
export function AiPanel({ getText, title, readOnly, onApplyText, onApplyTitle, onClose }: Props) {
  const [status, setStatus] = useState<AiStatus | null>(null);
  const [busy, setBusy] = useState<AiTask | null>(null);
  const [result, setResult] = useState<AiResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [seconds, setSeconds] = useState(30);
  const [lang, setLang] = useState<'el' | 'en'>('en');

  useEffect(() => {
    void aiStatus().then(setStatus);
  }, []);

  const run = async (task: AiTask) => {
    const text = getText().trim();
    if (!text) return setError(t('aiNoText'));
    setBusy(task);
    setError(null);
    try {
      const r = await post<AiResult & { ok: true }>('/api/ai/assist', { task, text, title: title || undefined, targetSeconds: task === 'shorten' ? seconds : undefined, targetLanguage: task === 'translate' ? lang : undefined });
      setResult(r);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const proposalWords = result?.text ? countWords(result.text) : 0;

  return (
    <aside className="ai-panel" aria-label={t('aiPanel')}>
      <div className="media-panel-head">
        <b>✦ {t('aiPanel')}</b>
        <span className="muted small">{status?.enabled ? status.model : ''}</span>
        <button type="button" className="media-panel-close" onClick={onClose} aria-label={t('close')} title={t('close')}>
          ✕
        </button>
      </div>
      {status && !status.enabled && <div className="banner warn">{t('aiDisabled')}</div>}
      <div className="ai-tasks">
        <button type="button" className="btn" disabled={!status?.enabled || !!busy} onClick={() => void run('proofread')}>
          {busy === 'proofread' ? '…' : t('aiProofread')}
        </button>
        <button type="button" className="btn" disabled={!status?.enabled || !!busy} onClick={() => void run('titles')}>
          {busy === 'titles' ? '…' : t('aiTitles')}
        </button>
        <span className="ai-inline">
          <button type="button" className="btn" disabled={!status?.enabled || !!busy} onClick={() => void run('shorten')}>
            {busy === 'shorten' ? '…' : t('aiShorten')}
          </button>
          <input type="number" min={5} max={1800} step={5} value={seconds} onChange={(e) => setSeconds(Number(e.target.value) || 30)} aria-label={t('aiTargetSeconds')} /> s
        </span>
        <button type="button" className="btn" disabled={!status?.enabled || !!busy} onClick={() => void run('summary')}>
          {busy === 'summary' ? '…' : t('aiSummary')}
        </button>
        <span className="ai-inline">
          <button type="button" className="btn" disabled={!status?.enabled || !!busy} onClick={() => void run('translate')}>
            {busy === 'translate' ? '…' : t('aiTranslate')}
          </button>
          <select value={lang} onChange={(e) => setLang(e.target.value as 'el' | 'en')} aria-label={t('language')}>
            <option value="en">EN</option>
            <option value="el">EL</option>
          </select>
        </span>
      </div>
      <p className="tip">{t('aiHint')}</p>
      {error && <div className="banner err">{error}</div>}
      {result && (
        <div className="ai-result">
          {result.task === 'titles' && (
            <ul className="ai-titles">
              {(result.titles ?? []).map((x, i) => (
                <li key={i}>
                  <span>{x}</span>
                  <button type="button" className="btn small" disabled={readOnly} onClick={() => onApplyTitle(x)}>
                    {t('aiUseTitle')}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {result.task === 'summary' && (
            <>
              <textarea readOnly value={result.summary ?? ''} rows={4} />
              <button type="button" className="btn small" onClick={() => void navigator.clipboard?.writeText(result.summary ?? '')}>
                {t('copy')}
              </button>
            </>
          )}
          {(result.task === 'proofread' || result.task === 'shorten' || result.task === 'translate') && (
            <>
              {result.task === 'proofread' && (
                <div className="ai-changes">
                  {(result.changes ?? []).length === 0 && <span className="status ok">{t('aiNoChanges')}</span>}
                  {(result.changes ?? []).map((c, i) => (
                    <div key={i} className="ai-change">
                      <s>{c.from}</s> → <b>{c.to}</b> <span className="muted">({c.reason})</span>
                    </div>
                  ))}
                </div>
              )}
              <textarea readOnly value={result.text ?? ''} rows={10} />
              <div className="toolbar left">
                <button type="button" className="btn primary" disabled={readOnly || !result.text} onClick={() => result.text && onApplyText(result.text)}>
                  {t('aiApply')}
                </button>
                <span className="muted small">
                  {proposalWords} {t('words')} · {t('readTime')} {mmss(Math.round(proposalWords / WORDS_PER_SECOND))}
                  {result.notes ? ` · ${result.notes}` : ''}
                </span>
              </div>
            </>
          )}
          <div className="muted small ai-usage">
            {result.model} · {result.usage.inputTokens + result.usage.outputTokens} tokens
          </div>
        </div>
      )}
    </aside>
  );
}
